// frontend/src/pages/Profile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { updateProfile, changePassword } from "../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Import WhatsApp from brands, not solid
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faUserCircle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faSave,
  faKey,
  faCheckCircle,
  faExclamationTriangle,
  faArrowLeft,
  faCity,
  faGlobe,
  faTractor,
  faStore,
  faCreditCard,
  faWallet,
  faBell,
  faHistory,
  faSpinner,
  faEdit,
  faTimes,
  faPlus,
  faTrash,
  faShippingFast,
  faBox,
  faCalendarAlt,
  faUserCheck,
  faShieldAlt,
  faLock,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../components/UI/Loader";
import "./profile.scss";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// MAIN COMPONENT
// ============================================
const Profile = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = useState("profile");
  const [showPassword, setShowPassword] = useState(false);

  // Profile Form State
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    address: {
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
    },
    preferredCurrency: "SLL",
    notificationsEnabled: true,
    emailNotifications: true,
    whatsappNotifications: true,
  });

  // Farmer-specific state
  const [farmData, setFarmData] = useState({
    farmDetails: {
      farmName: "",
      farmSize: "small",
      livestockType: [],
      animalCount: 0,
      yearsInOperation: 0,
    },
    bulkPurchaseEnabled: false,
    minimumBulkQuantity: 100,
    preferredPaymentMethod: "cash_on_delivery",
    creditLimit: 0,
    paymentTerms: 30,
  });

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Address Management
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [newAddress, setNewAddress] = useState({
    recipientName: "",
    street: "",
    city: "",
    district: "",
    country: "Sierra Leone",
    postalCode: "",
    phone: "",
    isDefault: false,
    label: "Home",
  });

  const isFarmer = user?.userType === "farmer" || user?.role === "farmer";

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || {
          street: "",
          city: "",
          district: "",
          country: "Sierra Leone",
        },
        preferredCurrency: user.preferredCurrency || "SLL",
        notificationsEnabled: user.notificationsEnabled !== false,
        emailNotifications: user.emailNotifications !== false,
        whatsappNotifications: user.whatsappNotifications !== false,
      });

      if (isFarmer) {
        setFarmData({
          farmDetails: user.farmDetails || {
            farmName: "",
            farmSize: "small",
            livestockType: [],
            animalCount: 0,
            yearsInOperation: 0,
          },
          bulkPurchaseEnabled: user.bulkPurchaseEnabled || false,
          minimumBulkQuantity: user.minimumBulkQuantity || 100,
          preferredPaymentMethod:
            user.preferredPaymentMethod || "cash_on_delivery",
          creditLimit: user.creditLimit || 0,
          paymentTerms: user.paymentTerms || 30,
        });
      }

      setAddresses(user.addresses || []);
    }
  }, [user, isFarmer]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setProfileData((prev) => ({ ...prev, [name]: checked }));
    } else if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setProfileData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFarmChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFarmData((prev) => ({ ...prev, [name]: checked }));
    } else if (name.startsWith("farmDetails.")) {
      const field = name.split(".")[1];
      setFarmData((prev) => ({
        ...prev,
        farmDetails: { ...prev.farmDetails, [field]: value },
      }));
    } else {
      setFarmData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLivestockToggle = (type) => {
    const currentTypes = farmData.farmDetails.livestockType || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];
    setFarmData((prev) => ({
      ...prev,
      farmDetails: { ...prev.farmDetails, livestockType: newTypes },
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setNewAddress({ ...newAddress, [name]: checked });
    } else {
      setNewAddress({ ...newAddress, [name]: value });
    }
  };

  const addAddress = () => {
    if (
      !newAddress.recipientName ||
      !newAddress.street ||
      !newAddress.city ||
      !newAddress.district
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required address fields",
      });
      return;
    }

    const updatedAddresses = [...addresses];
    if (newAddress.isDefault) {
      updatedAddresses.forEach((addr) => (addr.isDefault = false));
    }

    if (editingAddressIndex !== null) {
      updatedAddresses[editingAddressIndex] = { ...newAddress };
      setEditingAddressIndex(null);
    } else {
      updatedAddresses.push({ ...newAddress });
    }

    setAddresses(updatedAddresses);
    setNewAddress({
      recipientName: "",
      street: "",
      city: "",
      district: "",
      country: "Sierra Leone",
      postalCode: "",
      phone: "",
      isDefault: addresses.length === 0,
      label: "Home",
    });
    setShowAddressForm(false);
  };

  const editAddress = (index) => {
    setNewAddress({ ...addresses[index] });
    setEditingAddressIndex(index);
    setShowAddressForm(true);
  };

  const removeAddress = (index) => {
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    if (addresses[index]?.isDefault && updatedAddresses.length > 0) {
      updatedAddresses[0].isDefault = true;
    }
    setAddresses(updatedAddresses);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setMessage({ type: "", text: "" });

    try {
      const updateData = {
        ...profileData,
        addresses: addresses,
      };

      if (isFarmer) {
        updateData.farmDetails = farmData.farmDetails;
        updateData.bulkPurchaseEnabled = farmData.bulkPurchaseEnabled;
        updateData.minimumBulkQuantity = farmData.minimumBulkQuantity;
        updateData.preferredPaymentMethod = farmData.preferredPaymentMethod;
        updateData.creditLimit = farmData.creditLimit;
        updateData.paymentTerms = farmData.paymentTerms;
      }

      const response = await updateProfile(updateData);
      if (response.data.success) {
        const updatedUser = { ...user, ...updateData };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        if (setUser) setUser(updatedUser);

        setMessage({
          type: "success",
          text: "Profile updated successfully!",
        });

        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to update profile",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters",
      });
      return;
    }

    setUpdating(true);
    setMessage({ type: "", text: "" });

    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
      );
      setMessage({
        type: "success",
        text: "Password changed successfully! Please login again.",
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to change password",
      });
    } finally {
      setUpdating(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  const livestockOptions = [
    "Cattle",
    "Poultry",
    "Goats",
    "Sheep",
    "Pigs",
    "Mixed",
  ];

  const farmSizeOptions = [
    { value: "small", label: "Small (1-50 animals)" },
    { value: "medium", label: "Medium (51-200 animals)" },
    { value: "large", label: "Large (200+ animals)" },
  ];

  const paymentMethodOptions = [
    { value: "cash_on_delivery", label: "Cash on Delivery" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "orange_money", label: "Orange Money" },
    { value: "afrimoney", label: "Afrimoney" },
    { value: "wave", label: "WAVE" },
    { value: "credit", label: "Credit (30 days)" },
  ];

  const currencyOptions = [
    { value: "SLL", label: "SLL (Le)" },
    { value: "USD", label: "USD ($)" },
    { value: "MYR", label: "MYR (RM)" },
  ];

  const tabs = [
    { id: "profile", label: "Profile", icon: faUserCircle },
    { id: "security", label: "Security", icon: faKey },
    ...(isFarmer ? [{ id: "farm", label: "Farm", icon: faTractor }] : []),
    { id: "addresses", label: "Addresses", icon: faMapMarkerAlt },
    { id: "preferences", label: "Preferences", icon: faBell },
  ];

  return (
    <motion.div
      className="profile-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div className="header-content">
            <FontAwesomeIcon icon={faUserCircle} className="header-icon" />
            <div>
              <h1>Account Settings</h1>
              <p>Manage your personal information and security settings</p>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              className={`profile-alert ${message.type}`}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <FontAwesomeIcon
                icon={
                  message.type === "success"
                    ? faCheckCircle
                    : faExclamationTriangle
                }
              />
              <span>{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="profile-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? "active" : ""}
              onClick={() => setActiveTab(tab.id)}
            >
              <FontAwesomeIcon icon={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="profile-grid">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faUserCircle} /> Personal Information
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faUserCircle} /> Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faEnvelope} /> Email Address
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="disabled-input"
                    />
                    <small>
                      Email cannot be changed. Contact support for assistance.
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faPhone} /> Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Street Address
                  </label>
                  <input
                    type="text"
                    name="address.street"
                    value={profileData.address?.street || ""}
                    onChange={handleProfileChange}
                    placeholder="Enter your street address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faCity} /> City
                    </label>
                    <input
                      type="text"
                      name="address.city"
                      value={profileData.address?.city || ""}
                      onChange={handleProfileChange}
                      placeholder="Enter your city"
                    />
                  </div>
                  <div className="form-group">
                    <label>District</label>
                    <input
                      type="text"
                      name="address.district"
                      value={profileData.address?.district || ""}
                      onChange={handleProfileChange}
                      placeholder="Enter your district"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FontAwesomeIcon icon={faGlobe} /> Country
                    </label>
                    <input
                      type="text"
                      name="address.country"
                      value={profileData.address?.country || "Sierra Leone"}
                      onChange={handleProfileChange}
                      placeholder="Enter your country"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </motion.div>
          )}

          {/* Security Tab */}
          {activeTab === "security" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faKey} /> Security
              </h2>
              <form onSubmit={handlePasswordChange} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                    placeholder="Enter your current password"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            newPassword: e.target.value,
                          })
                        }
                        required
                        placeholder="Enter new password (min 6 characters)"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <FontAwesomeIcon
                          icon={showPassword ? faEyeSlash : faEye}
                        />
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                      placeholder="Confirm your new password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faKey} />
                  {updating ? "Changing..." : "Change Password"}
                </button>
              </form>

              <div className="security-tips">
                <h4>
                  <FontAwesomeIcon icon={faShieldAlt} /> Security Tips
                </h4>
                <ul>
                  <li>Use a strong password with at least 8 characters</li>
                  <li>Include numbers, symbols, and mixed case letters</li>
                  <li>Never share your password with anyone</li>
                  <li>Enable two-factor authentication for added security</li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Farm Tab */}
          {activeTab === "farm" && isFarmer && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faTractor} /> Farm Details
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-group">
                  <label>Farm/Business Name</label>
                  <input
                    type="text"
                    name="farmDetails.farmName"
                    value={farmData.farmDetails.farmName}
                    onChange={handleFarmChange}
                    placeholder="Enter your farm name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Farm Size</label>
                    <select
                      name="farmDetails.farmSize"
                      value={farmData.farmDetails.farmSize}
                      onChange={handleFarmChange}
                    >
                      {farmSizeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Animal Count</label>
                    <input
                      type="number"
                      name="farmDetails.animalCount"
                      value={farmData.farmDetails.animalCount}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Number of animals"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Livestock Type</label>
                  <div className="livestock-tags">
                    {livestockOptions.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`livestock-tag ${farmData.farmDetails.livestockType?.includes(type) ? "active" : ""}`}
                        onClick={() => handleLivestockToggle(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Years in Operation</label>
                    <input
                      type="number"
                      name="farmDetails.yearsInOperation"
                      value={farmData.farmDetails.yearsInOperation}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Years in operation"
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Payment Method</label>
                    <select
                      name="preferredPaymentMethod"
                      value={farmData.preferredPaymentMethod}
                      onChange={handleFarmChange}
                    >
                      {paymentMethodOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="bulkPurchaseEnabled"
                        checked={farmData.bulkPurchaseEnabled}
                        onChange={handleFarmChange}
                      />
                      Enable bulk purchases
                    </label>
                  </div>
                  <div className="form-group">
                    <label>Minimum Bulk Quantity (kg)</label>
                    <input
                      type="number"
                      name="minimumBulkQuantity"
                      value={farmData.minimumBulkQuantity}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Minimum bulk quantity"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Credit Limit (USD)</label>
                    <input
                      type="number"
                      name="creditLimit"
                      value={farmData.creditLimit}
                      onChange={handleFarmChange}
                      min="0"
                      step="0.01"
                      placeholder="Credit limit"
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms (days)</label>
                    <input
                      type="number"
                      name="paymentTerms"
                      value={farmData.paymentTerms}
                      onChange={handleFarmChange}
                      min="0"
                      placeholder="Payment terms in days"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {updating ? "Saving..." : "Save Farm Details"}
                </button>
              </form>
            </motion.div>
          )}

          {/* Addresses Tab */}
          {activeTab === "addresses" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Addresses
              </h2>

              <div className="addresses-list">
                {addresses.map((address, index) => (
                  <div key={index} className="address-item">
                    <div className="address-info">
                      <div className="address-label">
                        {address.label || "Address"}
                        {address.isDefault && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                      <div className="address-details">
                        {address.recipientName}
                        <br />
                        {address.street}
                        <br />
                        {address.city}, {address.district}
                        <br />
                        {address.country}
                        {address.phone && (
                          <>
                            <br />
                            <FontAwesomeIcon icon={faPhone} /> {address.phone}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="address-actions">
                      <button
                        type="button"
                        className="edit-btn"
                        onClick={() => editAddress(index)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeAddress(index)}
                        disabled={addresses.length <= 1}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {!showAddressForm ? (
                <button
                  type="button"
                  className="add-address-btn"
                  onClick={() => {
                    setShowAddressForm(true);
                    setEditingAddressIndex(null);
                    setNewAddress({
                      recipientName: profileData.name || "",
                      street: "",
                      city: "",
                      district: "",
                      country: "Sierra Leone",
                      postalCode: "",
                      phone: profileData.phone || "",
                      isDefault: addresses.length === 0,
                      label: "Home",
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Address
                </button>
              ) : (
                <div className="address-form">
                  <h4>
                    {editingAddressIndex !== null
                      ? "Edit Address"
                      : "Add New Address"}
                  </h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Recipient Name *</label>
                      <input
                        type="text"
                        name="recipientName"
                        value={newAddress.recipientName}
                        onChange={handleAddressChange}
                        placeholder="Recipient name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Label</label>
                      <input
                        type="text"
                        name="label"
                        value={newAddress.label}
                        onChange={handleAddressChange}
                        placeholder="e.g., Home, Office"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      name="street"
                      value={newAddress.street}
                      onChange={handleAddressChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        value={newAddress.city}
                        onChange={handleAddressChange}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>District *</label>
                      <input
                        type="text"
                        name="district"
                        value={newAddress.district}
                        onChange={handleAddressChange}
                        placeholder="District"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={newAddress.country}
                        onChange={handleAddressChange}
                        placeholder="Country"
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={newAddress.postalCode}
                        onChange={handleAddressChange}
                        placeholder="Postal code"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={newAddress.phone}
                      onChange={handleAddressChange}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isDefault"
                        checked={newAddress.isDefault}
                        onChange={handleAddressChange}
                      />
                      Set as default address
                    </label>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setShowAddressForm(false);
                        setEditingAddressIndex(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={addAddress}
                    >
                      <FontAwesomeIcon icon={faSave} /> Save Address
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Preferences Tab */}
          {activeTab === "preferences" && (
            <motion.div
              className="profile-card full-width"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2>
                <FontAwesomeIcon icon={faBell} /> Preferences
              </h2>
              <form onSubmit={handleProfileSubmit} className="profile-form">
                <div className="form-group">
                  <label>Preferred Currency</label>
                  <select
                    name="preferredCurrency"
                    value={profileData.preferredCurrency}
                    onChange={handleProfileChange}
                  >
                    {currencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notificationsEnabled"
                        checked={profileData.notificationsEnabled}
                        onChange={handleProfileChange}
                      />
                      <span>
                        <FontAwesomeIcon icon={faBell} /> Enable Notifications
                      </span>
                      <small>
                        Receive notifications from Kenali Enterprise
                      </small>
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="emailNotifications"
                        checked={profileData.emailNotifications}
                        onChange={handleProfileChange}
                      />
                      <span>
                        <FontAwesomeIcon icon={faEnvelope} /> Email
                        Notifications
                      </span>
                      <small>Receive email notifications</small>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="whatsappNotifications"
                      checked={profileData.whatsappNotifications}
                      onChange={handleProfileChange}
                    />
                    <span>
                      <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
                      Notifications
                    </span>
                    <small>Receive WhatsApp notifications</small>
                  </label>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updating}
                >
                  <FontAwesomeIcon icon={faSave} />
                  {updating ? "Saving..." : "Save Preferences"}
                </button>
              </form>
            </motion.div>
          )}
        </div>

        {/* Account Information */}
        <div className="profile-card account-info">
          <h2>Account Information</h2>
          <div className="account-details">
            <div className="account-detail">
              <span className="detail-label">Account Type:</span>
              <span className="detail-value">
                {user?.role === "admin" || user?.role === "super_admin"
                  ? "Administrator"
                  : user?.userType === "farmer"
                    ? "Farmer Account"
                    : user?.userType === "supplier"
                      ? "Supplier Account"
                      : "Customer Account"}
              </span>
            </div>
            <div className="account-detail">
              <span className="detail-label">Member Since:</span>
              <span className="detail-value">
                {user?.createdAt
                  ? new Date(user.createdAt).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
            <div className="account-detail">
              <span className="detail-label">Account Status:</span>
              <span
                className={`status-badge ${user?.isActive ? "active" : "inactive"}`}
              >
                {user?.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="account-detail">
              <span className="detail-label">Login Count:</span>
              <span className="detail-value">{user?.loginCount || 0}</span>
            </div>
            {user?.lastLogin && (
              <div className="account-detail">
                <span className="detail-label">Last Login:</span>
                <span className="detail-value">
                  {new Date(user.lastLogin).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>


      <style>{`
        /* ============================================
           PROFILE PAGE STYLES - Mobile-First
           ============================================ */

        .profile-page {
          min-height: 100vh;
          background: #f8f8f8;
          padding: 1.5rem 0;
        }

        @media (min-width: 768px) {
          .profile-page {
            padding: 2rem 0;
          }
        }

        .profile-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        @media (min-width: 768px) {
          .profile-container {
            padding: 0 2rem;
          }
        }

        /* ========== HEADER ========== */
        .profile-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .back-btn {
          background: none;
          border: none;
          color: #D9A321;
          cursor: pointer;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
        }

        .back-btn:hover {
          color: #B8860B;
          transform: translateX(-4px);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }

        .header-icon {
          font-size: 2rem;
          color: #D9A321;
        }

        .header-content h1 {
          font-size: 1.25rem;
          color: #006B2E;
          margin: 0;
          font-family: "Montserrat", sans-serif;
        }

        .header-content p {
          font-size: 0.813rem;
          color: #5A5A5A;
          margin: 0;
        }

        @media (min-width: 768px) {
          .header-content h1 {
            font-size: 1.5rem;
          }
        }

        /* ========== ALERT ========== */
        .profile-alert {
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .profile-alert.success {
          background: #dcfce7;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }

        .profile-alert.error {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        /* ========== TABS ========== */
        .profile-tabs {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
          overflow-x: auto;
          padding-bottom: 0.25rem;
        }

        .profile-tabs button {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          border: 2px solid transparent;
          background: transparent;
          color: #5A5A5A;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.813rem;
          transition: all 0.3s ease;
          white-space: nowrap;
          font-family: "Inter", sans-serif;
          min-height: 44px;
        }

        .profile-tabs button:hover {
          border-color: #D9A321;
        }

        .profile-tabs button.active {
          background: #D9A321;
          color: #ffffff;
          border-color: #D9A321;
          font-weight: 600;
        }

        .profile-tabs button svg {
          margin-right: 0.4rem;
        }

        @media (max-width: 480px) {
          .profile-tabs button {
            font-size: 0.75rem;
            padding: 0.35rem 0.75rem;
          }
        }

        /* ========== GRID ========== */
        .profile-grid {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .profile-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          border: 1px solid #e5e5e5;
        }

        @media (min-width: 768px) {
          .profile-card {
            padding: 1.5rem;
            border-radius: 16px;
          }
        }

        .profile-card.full-width {
          grid-column: 1 / -1;
        }

        .profile-card h2 {
          color: #006B2E;
          margin-bottom: 1.25rem;
          font-size: 1.125rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .profile-card h2 svg {
          color: #D9A321;
        }

        /* ========== FORM ========== */
        .profile-form,
        .password-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        @media (min-width: 576px) {
          .form-row {
            grid-template-columns: 1fr 1fr;
          }
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .form-group label {
          font-weight: 500;
          font-size: 0.813rem;
          color: #1F1F1F;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .form-group label svg {
          color: #D9A321;
          font-size: 0.7rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.625rem 0.75rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-size: 0.875rem;
          font-family: "Inter", sans-serif;
          transition: all 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
          min-height: 44px;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .form-group input.disabled-input {
          background: #f0f0f0;
          cursor: not-allowed;
        }

        .form-group small {
          font-size: 0.65rem;
          color: #5A5A5A;
        }

        .password-input-wrapper {
          position: relative;
        }

        .password-input-wrapper input {
          width: 100%;
          padding-right: 2.5rem;
        }

        .password-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #5A5A5A;
          cursor: pointer;
          padding: 0.25rem;
        }

        .password-toggle:hover {
          color: #006B2E;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 400;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #D9A321;
          cursor: pointer;
          min-height: auto;
        }

        .checkbox-label span {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .checkbox-label small {
          display: block;
          font-size: 0.65rem;
          color: #5A5A5A;
          font-weight: 400;
        }

        .btn-primary {
          padding: 0.625rem 1.5rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          min-height: 44px;
          font-family: "Inter", sans-serif;
        }

        .btn-primary:hover:not(:disabled) {
          background: #B8860B;
          transform: translateY(-2px);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-secondary {
          padding: 0.625rem 1.5rem;
          background: transparent;
          color: #5A5A5A;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
          min-height: 44px;
          font-family: "Inter", sans-serif;
        }

        .btn-secondary:hover {
          background: #f0f0f0;
        }

        /* ========== LIVESTOCK TAGS ========== */
        .livestock-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .livestock-tag {
          padding: 0.3rem 0.75rem;
          border-radius: 20px;
          border: 2px solid #e5e5e5;
          background: transparent;
          color: #5A5A5A;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          min-height: 36px;
        }

        .livestock-tag:hover {
          border-color: #D9A321;
        }

        .livestock-tag.active {
          border-color: #D9A321;
          background: rgba(217, 163, 33, 0.1);
          color: #006B2E;
        }

        /* ========== ADDRESSES ========== */
        .addresses-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .address-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 0.75rem 1rem;
          background: #F8F9FA;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .address-info {
          flex: 1;
        }

        .address-label {
          font-weight: 600;
          color: #1F1F1F;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .default-badge {
          font-size: 0.6rem;
          background: #D9A321;
          color: #ffffff;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
        }

        .address-details {
          font-size: 0.813rem;
          color: #5A5A5A;
          margin-top: 0.25rem;
          line-height: 1.6;
        }

        .address-details svg {
          color: #D9A321;
          font-size: 0.65rem;
        }

        .address-actions {
          display: flex;
          gap: 0.5rem;
        }

        .address-actions button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.375rem 0.5rem;
          border-radius: 6px;
          transition: all 0.3s ease;
          font-size: 0.875rem;
        }

        .edit-btn {
          color: #3b82f6;
        }

        .edit-btn:hover {
          background: #dbeafe;
        }

        .remove-btn {
          color: #dc2626;
        }

        .remove-btn:hover {
          background: #fee2e2;
        }

        .remove-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .add-address-btn {
          padding: 0.5rem 1rem;
          background: transparent;
          border: 2px dashed #e5e5e5;
          border-radius: 8px;
          color: #5A5A5A;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          width: 100%;
          font-family: "Inter", sans-serif;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .add-address-btn:hover {
          border-color: #D9A321;
          color: #006B2E;
        }

        .address-form {
          padding: 1rem;
          background: #F8F9FA;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          margin-top: 0.5rem;
        }

        .address-form h4 {
          margin-bottom: 1rem;
          color: #006B2E;
          font-family: "Montserrat", sans-serif;
        }

        .form-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.5rem;
          flex-wrap: wrap;
        }

        .form-actions button {
          flex: 1;
          min-width: 100px;
        }

        /* ========== SECURITY TIPS ========== */
        .security-tips {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e5e5;
        }

        .security-tips h4 {
          font-size: 0.938rem;
          color: #1F1F1F;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }

        .security-tips h4 svg {
          color: #D9A321;
        }

        .security-tips ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .security-tips li {
          padding: 0.25rem 0;
          font-size: 0.813rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .security-tips li::before {
          content: "•";
          color: #D9A321;
          font-weight: 700;
        }

        /* ========== ACCOUNT INFO ========== */
        .account-info {
          margin-top: 1.5rem;
        }

        .account-details {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.5rem;
        }

        @media (min-width: 576px) {
          .account-details {
            grid-template-columns: 1fr 1fr;
          }
        }

        .account-detail {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.875rem;
        }

        .account-detail:last-child {
          border-bottom: none;
        }

        .detail-label {
          color: #5A5A5A;
          font-weight: 400;
        }

        .detail-value {
          color: #1F1F1F;
          font-weight: 500;
        }

        .status-badge {
          padding: 0.1rem 0.5rem;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .status-badge.active {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-badge.inactive {
          background: #fee2e2;
          color: #dc2626;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 480px) {
          .profile-page {
            padding: 1rem 0;
          }

          .profile-container {
            padding: 0 0.75rem;
          }

          .profile-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .header-content {
            width: 100%;
          }

          .header-icon {
            font-size: 1.5rem;
          }

          .profile-card {
            padding: 1rem;
          }

          .profile-card h2 {
            font-size: 1rem;
          }

          .profile-tabs {
            gap: 0.25rem;
          }

          .profile-tabs button {
            font-size: 0.7rem;
            padding: 0.3rem 0.6rem;
          }

          .address-item {
            flex-direction: column;
            align-items: stretch;
          }

          .address-actions {
            justify-content: flex-end;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions button {
            min-width: auto;
          }

          .account-detail {
            flex-direction: column;
            gap: 0.15rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Profile;
